import type { NativeSession } from "../native/session";
import type { NativePointer } from "../native/pointer";
import type { Camera } from "../native/geometry";
import { nodeMatrix } from "../native/transforms";
import { invertAffine, transformPoint } from "../geometry/affine";
import { frameWarp, warpQuad } from "../native/deformation";
import { edgeOutline, zeroEdges } from "../geometry/edge-warp";
import {
  deformationPoints,
  hitDeformationHandle,
  deformHandle,
} from "../native/deformation-handles";
import type { Point, SceneNode } from "../scene/model";
import { originalIcon } from "./native-original-icons";
import { originalCompact } from "./native-original-controls";
export class NativeDeformation {
  private original: SceneNode | null = null;
  private temporary = false;
  private gesture: {
    node: SceneNode;
    start: Point;
    index: number;
    pointerId: number;
    temporary: boolean;
  } | null = null;
  private actions: HTMLElement;
  private section: HTMLElement;
  private row: HTMLElement;
  private prompt = document.createElement("dialog");
  constructor(
    private session: NativeSession,
    private pointer: NativePointer,
    private overlay: HTMLCanvasElement,
    private camera: Camera,
    private changed: () => void,
    private prepare: () => boolean,
  ) {
    this.section = document.createElement("div");
    this.section.className = "tool-section deformation-properties";
    this.section.id = "deformation-properties";
    this.section.innerHTML = `<button type="button" id="deformation-toggle" class="deformation-toggle" aria-pressed="false">${originalIcon("deform")}<span>Деформация</span></button>`;
    document
      .querySelector("#native-right-panel .native-panel-scroll")!
      .append(this.section);
    document.getElementById("deformation-toggle")!.onclick = () =>
      this.active ? this.requestExit() : this.begin();
    this.actions = document.createElement("div");
    this.actions.id = "deformation-actions";
    this.actions.className = "deformation-actions";
    this.actions.hidden = true;
    for (const [action, icon, title] of [
      ["cancel", "cancel", "Отменить деформацию"],
      ["apply", "confirm", "Применить деформацию"],
    ]) {
      const b = document.createElement("button");
      b.id = "deformation-" + action;
      b.title = b.ariaLabel = title!;
      b.innerHTML = originalIcon(icon!);
      if (action === "apply") b.className = "is-primary";
      b.onclick = () => this.finish(action === "apply");
      this.actions.append(b);
    }
    document.querySelector(".native-bottom-controls")!.append(this.actions);
    this.row = originalCompact("native-deformation-compact", [
      { id: "deformation-toggle", icon: "deform", title: "Деформация" },
    ]);
    this.prompt.innerHTML =
      '<h2>Деформация не завершена</h2><p>Применить изменения перед выходом?</p><button data-action="apply">Применить</button><button data-action="cancel">Не применять</button><button data-action="stay">Продолжить редактирование</button>';
    document.getElementById("native-editor")!.append(this.prompt);
    this.prompt.querySelectorAll<HTMLButtonElement>("button").forEach(
      (b) =>
        (b.onclick = () => {
          this.prompt.close();
          if (b.dataset.action !== "stay")
            this.finish(b.dataset.action === "apply");
        }),
    );
    for (const type of [
      "pointerdown",
      "pointermove",
      "pointerup",
      "pointercancel",
      "lostpointercapture",
    ] as const)
      document.addEventListener(type, (e) => this.event(e), true);
    window.addEventListener(
      "keydown",
      (e) => {
        if (!this.active) return;
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopImmediatePropagation();
          if (this.prompt.open) this.prompt.close();
          else this.finish(false);
        } else if (
          this.editing &&
          e.key === "Enter" &&
          !(e.target as HTMLElement).closest("input,textarea,button")
        ) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.finish(true);
        }
      },
      true,
    );
    window.addEventListener("blur", () => {
      this.endGesture(false);
    });
  }
  get active() {
    return !!this.original;
  }
  get gesturing() {
    return !!this.gesture;
  }
  get editing() {
    return this.active && !this.temporary;
  }
  private begin(temporary = false) {
    if (
      this.active ||
      !this.session.selected ||
      (temporary && this.session.selected.text.frame) ||
      !this.prepare()
    )
      return false;
    if (!temporary) this.pointer.setTool("select");
    this.temporary = temporary;
    this.original = this.session.selected!;
    this.session.begin();
    this.changed();
    return true;
  }
  finish(apply: boolean) {
    const n = this.session.selected;
    if (this.active) {
      if (apply && n && n !== this.original) {
        if (!this.temporary && n.geometry.warpCorners)
          this.session.replaceNode(frameWarp(n));
        this.session.finish();
      } else this.session.cancel();
    }
    this.original = null;
    this.temporary = false;
    const gesture = this.gesture;
    this.gesture = null;
    if (gesture && this.overlay.hasPointerCapture(gesture.pointerId))
      this.overlay.releasePointerCapture(gesture.pointerId);
    this.prompt.close();
    this.changed();
  }
  private requestExit() {
    if (this.session.selected === this.original) this.finish(false);
    else if (!this.prompt.open) this.prompt.showModal();
  }
  private endGesture(apply: boolean) {
    const g = this.gesture;
    if (!g) return;
    if (g.temporary) this.finish(apply);
    else {
      this.gesture = null;
      if (!apply) this.session.replaceNode(g.node);
      if (this.overlay.hasPointerCapture(g.pointerId))
        this.overlay.releasePointerCapture(g.pointerId);
      this.changed();
    }
  }
  private point(e: PointerEvent) {
    const rect = this.overlay.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left - this.camera.x) / this.camera.zoom,
      y: (e.clientY - rect.top - this.camera.y) / this.camera.zoom,
    };
  }
  private event(e: PointerEvent) {
    if (this.prompt.open) return;
    const target = e.target as HTMLElement;
    if (
      e.type === "pointerdown" &&
      this.editing &&
      !target.closest(
        "#native-overlay,#deformation-properties,#deformation-actions,#native-deformation-compact",
      )
    ) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.requestExit();
      return;
    }
    if (target !== this.overlay && !this.gesture) return;
    if (this.pointer.space || e.button === 1) return;
    const node = this.session.selected;
    if (!node) return;
    if (e.type === "pointerdown") {
      if (this.gesture) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      if (e.button !== 0 || (!this.active && !e.ctrlKey && !e.metaKey)) return;
      const index = hitDeformationHandle(
        node,
        this.point(e),
        this.camera.zoom,
        e.pointerType === "touch",
        this.editing,
      );
      if (index < 0) {
        if (this.active) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
        return;
      }
      const temporary = !this.active;
      const inv = invertAffine(nodeMatrix(node));
      if (!inv) return;
      if (temporary && !this.begin(true)) return;
      this.overlay.focus();
      this.gesture = {
        node,
        start: transformPoint(this.point(e), inv),
        index,
        pointerId: e.pointerId,
        temporary,
      };
      this.overlay.setPointerCapture(e.pointerId);
    } else if (this.gesture?.pointerId === e.pointerId) {
      const g = this.gesture;
      if (e.type === "pointermove") {
        const inv = invertAffine(nodeMatrix(g.node))!,
          p = transformPoint(this.point(e), inv),
          next = deformHandle(
            g.node,
            g.index,
            { x: p.x - g.start.x, y: p.y - g.start.y },
            e.altKey && (!g.temporary || g.node.kind !== "image"),
            !g.temporary,
          );
        this.session.replaceNode(
          g.temporary && next !== g.node && next.geometry.warpCorners
            ? frameWarp(next)
            : next,
        );
      } else if (
        ["pointerup", "pointercancel", "lostpointercapture"].includes(e.type)
      ) {
        this.endGesture(e.type === "pointerup");
      }
    } else return;
    e.preventDefault();
    e.stopImmediatePropagation();
    this.changed();
  }
  render(ctx: CanvasRenderingContext2D) {
    const n = this.session.selected;
    this.section.hidden = !n;
    this.row.hidden = this.section.hidden;
    const parent = document.querySelector(
      "#native-compact-properties > .native-property-row",
    );
    if (parent && this.row.parentElement !== parent) parent.append(this.row);
    this.actions.hidden = !this.editing;
    document
      .getElementById("deformation-toggle")!
      .setAttribute("aria-pressed", String(this.editing));
    if (this.gesture?.temporary && !this.pointer.space)
      this.overlay.style.cursor = "crosshair";
    if (!this.active || !n) return;
    const points = deformationPoints(n).map((p) => ({
      x: p.x * this.camera.zoom + this.camera.x,
      y: p.y * this.camera.zoom + this.camera.y,
    }));
    ctx.save();
    ctx.strokeStyle = "#86aef4";
    ctx.fillStyle = "#7ea7ef";
    ctx.lineWidth = 1;
    ctx.setLineDash([]);
    ctx.beginPath();
    edgeOutline(warpQuad(n), n.geometry.warpEdges ?? zeroEdges()).forEach(
      (p, i) => {
        const w = transformPoint(p, nodeMatrix(n)),
          x = w.x * this.camera.zoom + this.camera.x,
          y = w.y * this.camera.zoom + this.camera.y;
        if (i) ctx.lineTo(x, y);
        else ctx.moveTo(x, y);
      },
    );
    ctx.closePath();
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = "#7c3aed";
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 1.75;
    points.forEach((p, i) => {
      if (n.kind === "text" && this.temporary && i % 2 === 0) return;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    });
    ctx.restore();
    this.overlay.style.cursor = this.gesture ? "move" : "crosshair";
  }
}
